import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { auth } from "@/lib/firebase";
import { onAuthStateChanged, signOut as firebaseSignOut, signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";

const AuthContext = createContext(null);

export function normalizeAuthRedirect(redirect) {
  if (typeof redirect !== "string") return "";
  const trimmed = redirect.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return "";
  return trimmed;
}

export function getDefaultAuthDestination(user) {
  if (user?.role === "admin") return "/admin";
  if (user?.role === "owner") return "/owner";
  return "/search";
}

export function getPostAuthDestination(user, redirect) {
  const dest = normalizeAuthRedirect(redirect) || getDefaultAuthDestination(user);
  if (user?.role === "owner" && !user?.phone_verified && window.location.pathname !== "/verify-phone") {
    return "/verify-phone";
  }
  return dest;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("cb_user", JSON.stringify(data));
    } catch {
      setUser(null);
      localStorage.removeItem("cb_user");
      localStorage.removeItem("cb_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("access_token=") || hash.includes("error=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const token = await firebaseUser.getIdToken();
        localStorage.setItem("cb_token", token);
        await checkAuth();
      } else {
        setUser(null);
        localStorage.removeItem("cb_user");
        localStorage.removeItem("cb_token");
      }
    });

    return () => unsubscribe();
  }, [checkAuth]);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await cred.user.getIdToken();
    const { data } = await api.post("/auth/login", { id_token: idToken });
    if (data.token) {
      localStorage.setItem("cb_token", data.token);
    }
    if (data.user) {
      localStorage.setItem("cb_user", JSON.stringify(data.user));
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (payload, options = {}) => {
    const onStatusChange = typeof options === "function" ? options : options.onStatusChange;
    onStatusChange?.("creatingAccount");
    const { data } = await api.post("/auth/register", payload);
    onStatusChange?.("signingYouIn");
    const cred = await signInWithEmailAndPassword(auth, payload.email, payload.password);
    const idToken = await cred.user.getIdToken();
    const loginResponse = await api.post("/auth/login", { id_token: idToken });
    if (loginResponse.data?.token) {
      localStorage.setItem("cb_token", loginResponse.data.token);
    }
    const resolvedUser = loginResponse.data?.user || data.user;
    if (resolvedUser) {
      localStorage.setItem("cb_user", JSON.stringify(resolvedUser));
    }
    setUser(resolvedUser);
    return resolvedUser;
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (e) {
      if (import.meta.env.DEV) console.warn("firebaseSignOut failed:", e);
    }
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (import.meta.env.DEV) console.warn("API logout failed:", e);
    }
    localStorage.removeItem("cb_token");
    localStorage.removeItem("cb_refresh_token");
    localStorage.removeItem("cb_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
